import { useCallback, useContext, useEffect, useState } from "react";

import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";
import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { PLANNING_TOOLTIP_MESSAGES } from "../../../../config";
import {
  clearWorkers,
  deleteWorkScopeAspects,
  patchDayDetails,
} from "../../../../services/api/dispatch";
import { setPlanningDayLocked } from "../../../../services/api/planning";
import {
  formatDate,
  getDateString,
  getIsLastDay,
} from "../../../../utils/datetime";
import { toLocalDateKey } from "../../../../utils/datetime/core";
import { isActiveCrewWorker } from "../../../../utils/business/worker";

/**
 * Build Dispatch-specific data/actions for one shared DayDetails card.
 *
 * @param {Object} params
 * @param {Object} params.workerAssignments Worker assignment summary for this date.
 * @param {Array<Object>} params.availableWorkers Full Dispatch worker catalog.
 *   DayDetails assigned/off-work/available lists show only active crew members.
 * @param {number} params.index Date index in the current Dispatch week.
 * @param {Date} params.date Calendar date represented by this summary.
 * @returns {Object}
 */
export default function useDispatchDayDetailsActions({
  workerAssignments,
  availableWorkers,
  index,
  date,
}) {
  const { showWeekends, scopes, reloadScopes } = useContext(WeeklyContext);
  const {
    workers,
    dayDetails,
    reloadDays,
    copyScopesDate,
    setCopyScopesDate,
    pasteScopes,
    scopesToDelete,
    setScopesToDelete,
    runPlanningOperation,
    getDispatchWeather,
  } = useContext(DispatchContext);

  const [isConfirmPasteModalOpen, setIsConfirmPasteModalOpen] = useState(false);
  const [isWorkerModalOpen, setIsWorkerModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [isClearAllWorkersModalOpen, setIsClearAllWorkersModalOpen] =
    useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false);
  const [isDispatchModalOpen, setIsDispatchModalOpen] = useState(false);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const allAssignedWorkers = workerAssignments?.allWorkers ?? [];
  const assignedWorkerIds = new Set(allAssignedWorkers.map((worker) => worker.id));
  const assignedWorkers = allAssignedWorkers.filter(isActiveCrewWorker);
  const uniqueWorkers = (availableWorkers ?? []).filter(
    (worker) => !assignedWorkerIds.has(worker.id)
  );
  const offWorkWorkers = (workers ?? []).filter(
    (worker) =>
      selectedWorkerIds.includes(worker.id) && isActiveCrewWorker(worker)
  );
  const displayAvailableWorkers = uniqueWorkers.filter(
    (worker) =>
      !selectedWorkerIds.includes(worker.id) && isActiveCrewWorker(worker)
  );
  const crewWorkers = (availableWorkers ?? []).filter(isActiveCrewWorker);
  const scopesForDate = scopes
    ? scopes.filter((scope) => scope.date === formatDate(date, "YYYY-MM-DD"))
    : [];
  const currentDayDetails = dayDetails?.[index] ?? null;
  const isLocked = Boolean(currentDayDetails?.locked);
  const isLastDay = getIsLastDay(date, showWeekends);
  const dateKey = toLocalDateKey(date);
  const menuAlign = isLastDay ? "right" : "left";

  const handlePasteScopes = useCallback(() => {
    pasteScopes("day", null, getDateString(date))
      .then(() => setIsConfirmPasteModalOpen(false));
  }, [pasteScopes, date]);

  /** Open paste confirmation only when the target day already has scopes. */
  const onPasteScopes = useCallback(() => {
    if (scopesForDate.length > 0) {
      setIsConfirmPasteModalOpen(true);
    } else {
      handlePasteScopes();
    }
  }, [handlePasteScopes, scopesForDate.length]);

  const openWorkerModal = useCallback(() => {
    setSelectedDate(date);
    setIsWorkerModalOpen(true);
  }, [date]);

  const handleClearAllWorkers = useCallback(async () => {
    await runPlanningOperation({
      module: "dispatch",
      operation: "clearWorkers",
      resources: ["dispatch.aspects"],
      apiCall: () => clearWorkers(date),
    });
    setIsClearAllWorkersModalOpen(false);
    reloadScopes();
  }, [date, reloadScopes, runPlanningOperation]);

  const onDeleteAllScopes = useCallback(() => {
    setIsConfirmDeleteModalOpen(true);
    const scopeIdsForDate = (scopes ?? [])
      .filter((scope) => scope.date === formatDate(date, "YYYY-MM-DD"))
      .map((scope) => scope.id);
    setScopesToDelete(scopeIdsForDate);
  }, [date, scopes, setScopesToDelete]);

  const handleDeleteAllScopes = useCallback(async () => {
    if (scopesToDelete !== null) {
      await runPlanningOperation({
        module: "dispatch",
        operation: "deleteDayScopes",
        resources: ["dispatch.aspects"],
        entityIds: scopesToDelete,
        apiCall: () => deleteWorkScopeAspects(scopesToDelete),
      });
      setIsConfirmDeleteModalOpen(false);
      reloadScopes();
    }
  }, [scopesToDelete, reloadScopes, runPlanningOperation]);

  const updateOffWorkWorkers = useCallback(
    async (newWorkerIds) => {
      await runPlanningOperation({
        module: "dispatch",
        operation: "updateDayOffWork",
        resources: ["dispatch.day_details"],
        apiCall: () => patchDayDetails(formatDate(date, "MM-DD-YYYY"), newWorkerIds),
      });
      await reloadDays();
    },
    [reloadDays, date, runPlanningOperation]
  );

  const submitOffWorkWorkers = useCallback(
    (submittedWorkerIds) => {
      setSelectedWorkerIds(submittedWorkerIds);
      setIsWorkerModalOpen(false);
      updateOffWorkWorkers(submittedWorkerIds);
    },
    [updateOffWorkWorkers]
  );

  useEffect(() => {
    if (dayDetails) {
      const day = Array.isArray(dayDetails)
        ? dayDetails.find((dayRow) => dayRow.date === formatDate(date, "YYYY-MM-DD"))
        : null;
      if (day) {
        setSelectedWorkerIds(day.off_work.map((worker) => worker.id));
      }
    }
  }, [dayDetails, date]);

  const openDispatchModal = useCallback(() => {
    setSelectedDate(date);
    setIsDispatchModalOpen(true);
    setSelectedWorkers(workerAssignments);
  }, [date, workerAssignments]);

  const openBroadcastModal = useCallback(() => {
    setIsBroadcastModalOpen(true);
  }, []);

  const copyDateMatch = getDateString(copyScopesDate) === getDateString(date);
  const scopeCopyText = copyDateMatch
    ? `Cancel ${String(copyScopesDate).split(" ")[0]} Scope Copy (Esc)`
    : `${copyScopesDate ? `Paste Copied Scopes from ${String(copyScopesDate).split(" ")[0]} to ${String(date).split(" ")[0]}` : `Copy All ${String(date).split(" ")[0]} Scopes`}`;
  const copyAction = copyDateMatch
    ? () => setCopyScopesDate(null)
    : copyScopesDate
      ? () => onPasteScopes()
      : () => setCopyScopesDate(date);

  const broadcastDayData = getBroadcastDayData({
    date,
    scopes,
    selectedWorkerIds,
    uniqueWorkers,
    workerAssignments,
  });

  const handleUnlockDay = useCallback(async () => {
    await runPlanningOperation({
      module: "dispatch",
      operation: "updatePlanningDayLock",
      resources: ["dispatch.day_details"],
      apiCall: () => setPlanningDayLocked(formatDate(date, "MM-DD-YYYY"), false),
    });
    await reloadDays();
  }, [date, reloadDays, runPlanningOperation]);

  const handleLockDay = useCallback(async () => {
    await runPlanningOperation({
      module: "dispatch",
      operation: "updatePlanningDayLock",
      resources: ["dispatch.day_details"],
      apiCall: () => setPlanningDayLocked(formatDate(date, "MM-DD-YYYY"), true),
    });
    await reloadDays();
  }, [date, reloadDays, runPlanningOperation]);

  const menuItems = {
    "Complete Dispatch": () => openDispatchModal(index),
    "Complete Daily Review": () => setIsReviewModalOpen(true),
    [isLocked ? "Unlock Day" : "Lock Day"]: isLocked ? handleUnlockDay : handleLockDay,
    "Broadcast SMS": () => openBroadcastModal(),
    "Smart Assign": null,
    [scopeCopyText]: () => copyAction(),
    "Clear All Workers": () => setIsClearAllWorkersModalOpen(true),
    "Delete All Scopes": () => onDeleteAllScopes(),
  };
  const disabledMenuItems = isLocked
    ? [
      "Complete Dispatch",
      "Complete Daily Review",
      "Broadcast SMS",
      "Smart Assign",
      "Clear All Workers",
      "Delete All Scopes",
    ]
    : [];

  return {
    shouldRender: Boolean(workerAssignments && availableWorkers && dayDetails),
    dayDetails: currentDayDetails,
    weather: getDispatchWeather(dateKey),
    assignedWorkers,
    offWorkWorkers,
    availableWorkers: displayAvailableWorkers,
    menuItems,
    disabledMenuItems,
    menuAlign,
    title: null,
    cardClassName: "",
    isLocked,
    isLastDay,
    offWorkDisabled: isLocked,
    offWorkTitle: isLocked
      ? PLANNING_TOOLTIP_MESSAGES.editingDisabledWhenLocked
      : undefined,
    openWorkerModal,
    workerModal: {
      isOpen: isWorkerModalOpen,
      onClose: () => setIsWorkerModalOpen(false),
      onSubmit: submitOffWorkWorkers,
      date: selectedDate,
      usedWorkers: allAssignedWorkers,
      selectedWorkerIds: Array.from(selectedWorkerIds),
      workers: crewWorkers,
    },
    dispatchModal: {
      date,
      selectedWorkers,
      isOpen: isDispatchModalOpen,
      onClose: () => setIsDispatchModalOpen(false),
    },
    broadcastModal: {
      date,
      dayData: broadcastDayData,
      isOpen: isBroadcastModalOpen,
      onClose: () => setIsBroadcastModalOpen(false),
    },
    reviewModal: {
      isOpen: isReviewModalOpen,
      onClose: () => setIsReviewModalOpen(false),
      date,
    },
    deleteModal: {
      isOpen: isConfirmDeleteModalOpen,
      title: `Confirm Delete All Scopes for ${date.toLocaleDateString("en-US", { weekday: "long" })}`,
      list: getScopeTitlesForDate(scopes, date),
      text: "Are you sure you want to delete these scopes?",
      buttonText: "Delete",
      onConfirm: handleDeleteAllScopes,
      onClose: () => {
        setIsConfirmDeleteModalOpen(false);
        setScopesToDelete([]);
      },
    },
    pasteModal: {
      isOpen: isConfirmPasteModalOpen,
      title: "Paste Scopes",
      text: "This will delete these existing scopes for this day and paste the copied scopes.",
      list: scopesForDate.map((scope) => scope.scope?.title || "Untitled Scope"),
      buttonText: "Confirm",
      onConfirm: handlePasteScopes,
      onClose: () => setIsConfirmPasteModalOpen(false),
    },
    clearWorkersModal: {
      isOpen: isClearAllWorkersModalOpen,
      title: "Clear all Workers?",
      text: `This will clear all workers assigned to the above scopes on ${date.toLocaleDateString("en-US", { weekday: "long" })}.`,
      list: scopesForDate.map((scope) => scope.scope?.title || "Untitled Scope"),
      buttonText: "Confirm",
      onConfirm: handleClearAllWorkers,
      onClose: () => setIsClearAllWorkersModalOpen(false),
    },
  };
}

/**
 * Build modal data for Broadcast SMS select-by groups.
 *
 * @param {Object} params
 * @param {Date} params.date Calendar date.
 * @param {Array<Object>} params.scopes Dispatch scopes.
 * @param {Array<number>} params.selectedWorkerIds Off-work worker ids.
 * @param {Array<Object>} params.uniqueWorkers Workers not assigned to scopes.
 * @param {Object} params.workerAssignments Worker assignment summary.
 * @returns {Object}
 */
function getBroadcastDayData({
  date,
  scopes,
  selectedWorkerIds,
  uniqueWorkers,
  workerAssignments,
}) {
  const dateStr = formatDate(date, "YYYY-MM-DD");
  const broadcastAssignedIds = workerAssignments?.allWorkers?.map((w) => w.id) ?? [];
  const broadcastAvailableIds = uniqueWorkers
    .filter((w) => !selectedWorkerIds.includes(w.id))
    .map((w) => w.id);
  const scopesForBroadcast =
    scopes?.filter((s) => s.date === dateStr && !s.skipped) ?? [];
  const jobsForDayMap = new Map();
  scopesForBroadcast.forEach((scope) => {
    const jobId = scope.job?.id;
    if (!jobId) return;
    const workerIds = (scope.worker_assignment ?? []).map((w) => w.id).filter(Boolean);
    if (!jobsForDayMap.has(jobId)) {
      jobsForDayMap.set(jobId, { job: scope.job, workerIds: [] });
    }
    const entry = jobsForDayMap.get(jobId);
    workerIds.forEach((id) => {
      if (!entry.workerIds.includes(id)) entry.workerIds.push(id);
    });
  });

  return {
    assignedWorkerIds: broadcastAssignedIds,
    offWorkWorkerIds: selectedWorkerIds,
    availableWorkerIds: broadcastAvailableIds,
    jobsForDay: Array.from(jobsForDayMap.values()),
  };
}

/**
 * Build scope titles for day-level confirmation modals.
 *
 * @param {Array<Object>} scopes Dispatch scopes.
 * @param {Date} date Calendar date.
 * @returns {Array<string>}
 */
function getScopeTitlesForDate(scopes, date) {
  return scopes
    ? scopes
      .filter((scope) => scope.date === formatDate(date, "YYYY-MM-DD"))
      .map((scope) => scope.scope?.title || "Untitled Scope")
    : [];
}
